export const RETURN_PARAM = "from";

const PATH_LABELS = {
  "/dashboard": "Ops Overview",
  "/dashboard/cases": "Collection Cases",
  "/dashboard/customers": "Customers",
  "/dashboard/contact-centre": "Contact Centre",
  "/dashboard/reports": "Reports",
  "/dashboard/escalations": "Escalations",
  "/dashboard/team": "Team",
  "/dashboard/roles": "Roles & permissions",
  "/dashboard/institutions": "Institutions",
  "/dashboard/settlements": "Settlement Requests",
  "/dashboard/settings": "Settings",
};

export function isSafeReturnPath(path) {
  if (!path || typeof path !== "string") return false;
  const pathname = path.split("?")[0];
  return pathname.startsWith("/dashboard") && !path.includes("//") && !path.includes("://");
}

export function getReturnTarget(searchParams, fallback, locationState) {
  const fromState = locationState?.from;
  if (isSafeReturnPath(fromState)) return fromState;
  const from = searchParams.get(RETURN_PARAM);
  if (isSafeReturnPath(from)) return from;
  return fallback;
}

export function getReturnLabel(path) {
  if (!path) return "previous page";
  const pathname = path.split("?")[0];

  if (PATH_LABELS[pathname]) return PATH_LABELS[pathname];
  if (/^\/dashboard\/institutions\/[^/]+\/portfolio-groups/i.test(pathname)) return "Portfolio groups";
  if (/^\/dashboard\/institutions\/[^/]+\/customers/i.test(pathname)) return "Institution customers";
  if (/^\/dashboard\/institutions\/[^/]+$/i.test(pathname)) return "Institution Details";
  if (pathname.startsWith("/dashboard/institutions/onboard")) return "Onboard Institution";
  if (pathname.startsWith("/dashboard/log-contact")) return "Log Contact";
  if (pathname.startsWith("/dashboard/escalations")) return "Escalations";
  if (pathname.startsWith("/dashboard/escalate-case")) return "Escalate Case";
  if (pathname.startsWith("/dashboard/request-settlement")) return "Request Settlement";

  const segment = pathname.split("/").filter(Boolean).pop();
  if (!segment) return "previous page";
  return segment.replace(/-/g, " ");
}

/** @deprecated Prefer navigateWithReturn / linkWithReturn for clean URLs (no ?from=). */
export function appendReturnTo(to, location) {
  if (!to || !location) return to;
  return to.split("?")[0];
}

function splitLocation(to) {
  const raw = String(to || "");
  const hashIndex = raw.indexOf("#");
  const hash = hashIndex >= 0 ? raw.slice(hashIndex) : "";
  const beforeHash = hashIndex >= 0 ? raw.slice(0, hashIndex) : raw;
  const queryIndex = beforeHash.indexOf("?");
  const pathname = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const search = queryIndex >= 0 ? beforeHash.slice(queryIndex) : "";

  return { pathname, search, hash };
}

function returnState(location, existing) {
  const from = location ? `${location.pathname}${location.search || ""}` : null;
  const state = { ...(existing && typeof existing === "object" ? existing : {}) };
  if (isSafeReturnPath(from)) state.from = from;
  return state;
}

/**
 * React Router navigate with return stack in location.state (clean address bar).
 */
export function navigateWithReturn(navigate, to, location) {
  if (!navigate || !to) return;
  if (typeof to === "object") {
    navigate(to, {
      state: returnState(location, to.state || location?.state),
    });
    return;
  }
  const { pathname, search, hash } = splitLocation(to);
  navigate(
    { pathname, search, hash },
    { state: returnState(location, location?.state) }
  );
}

/**
 * Link `to` prop with return stack in state.
 * Accepts a path string or a location object already produced by this helper.
 */
export function linkWithReturn(to, location) {
  if (to && typeof to === "object" && typeof to.pathname === "string") {
    return {
      ...to,
      state: returnState(location, to.state),
    };
  }

  const { pathname, search, hash } = splitLocation(to);
  return { pathname, search, hash, state: returnState(location) };
}

export function stripReturnParam(searchParams) {
  const params = new URLSearchParams(searchParams);
  params.delete(RETURN_PARAM);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}
